<template>
  <div class="material-list-display">
    <div v-if="$slots.headerActions || config?.searchField" class="header-actions">
      <slot name="headerActions" />
      <div v-if="config?.searchField" class="material-search">
        <el-input
          v-model="searchValue"
          :placeholder="config.searchField.placeholder"
          clearable
          class="material-search-input"
          @keyup.enter="onSearch"
          @clear="onSearch"
        />
        <el-button type="primary" @click="onSearch">搜索</el-button>
      </div>
    </div>
    <!-- ── 菜单卡片布局（问答菜单 / 文本菜单）────────────────────────────── -->
    <template v-if="isMenuType">
      <div v-loading="loading" class="card-grid">
        <template v-if="list.length">
          <el-card
            v-for="(item, idx) in list"
            :key="idx"
            class="menu-card cursor-pointer"
            :class="{ 'is-selected': isSelected(getMenuId(item)) }"
            shadow="never"
            :body-style="{ padding: '12px' }"
            @click="toggleSelect(getMenuId(item))"
          >
            <div v-if="showOrder && isSelected(getMenuId(item))" class="order-badge">
              {{ getOrderIndex(getMenuId(item)) }}
            </div>
            <div class="card-title">{{ asMenu(item).title || '素材名称' }}</div>
            <ul class="menu-info">
              <li v-if="asMenu(item).guide_words" class="menu-info-row">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="asMenu(item).guide_words" />
              </li>
              <li
                v-for="(q, qi) in parseQuestions(asMenu(item).questions)"
                :key="qi"
                class="menu-info-row"
              >
                <span>{{ getQuestionTitle(q) }}</span>
              </li>
              <li v-if="parseMeta(asMenu(item).questions)?.conclusion" class="menu-info-row">
                <span>{{ parseMeta(asMenu(item).questions)?.conclusion }}</span>
              </li>
            </ul>
          </el-card>
        </template>
        <el-empty v-else class="grid-empty" :description="emptyText" />
      </div>
      <TablePagination
        v-if="total > 0"
        v-model:page="innerPage"
        v-model:page-size="innerPageSize"
        :total="total"
        :page-sizes="[12, 24]"
        @page-change="onPaginationChange"
        @size-change="onPaginationChange"
      />
    </template>

    <!-- ── 图片卡片布局（图片库素材，全渠道通用）──────────────────────── -->
    <template v-else-if="isImageType">
      <div v-loading="loading" class="card-grid">
        <template v-if="list.length">
          <el-card
            v-for="(item, idx) in list"
            :key="idx"
            class="image-card cursor-pointer"
            :class="{ 'is-selected': isSelected(asImage(item).id) }"
            shadow="never"
            :body-style="{ padding: '12px' }"
            @click="toggleSelect(asImage(item).id)"
          >
            <div v-if="showOrder && isSelected(asImage(item).id)" class="order-badge">
              {{ getOrderIndex(asImage(item).id as RawId) }}
            </div>
            <div class="image-card-img">
              <img v-if="imageSrc(item)" :src="imageSrc(item)" class="image-card-img-inner" />
              <span v-else>图片</span>
            </div>
            <div class="image-card-name" :title="asImage(item).name">
              {{ asImage(item).name || '图片名称' }}
            </div>
          </el-card>
        </template>
        <el-empty v-else class="grid-empty" :description="emptyText" />
      </div>
      <TablePagination
        v-if="total > 0"
        v-model:page="innerPage"
        v-model:page-size="innerPageSize"
        :total="total"
        :page-sizes="[12, 24]"
        @page-change="onPaginationChange"
        @size-change="onPaginationChange"
      />
    </template>

    <!-- ── 留资卡预览布局（网站留资卡）──────────────────────────────────── -->
    <template v-else-if="isWebLeadCard">
      <div v-loading="loading" class="card-grid">
        <template v-if="list.length">
          <el-card
            v-for="(item, idx) in list"
            :key="idx"
            class="lead-card cursor-pointer"
            :class="{ 'is-selected': isSelected(asLeadCard(item).id ?? idx) }"
            shadow="never"
            :body-style="{ padding: '12px' }"
            @click="toggleSelect(asLeadCard(item).id ?? idx)"
          >
            <div v-if="showOrder && isSelected(asLeadCard(item).id ?? idx)" class="order-badge">
              {{ getOrderIndex(asLeadCard(item).id ?? idx) }}
            </div>
            <div class="lead-card-img">
              <img
                v-if="asLeadCard(item).image_url"
                :src="asLeadCard(item).image_url as string"
                class="lead-card-img-inner"
              />
              <span v-else>卡片图片</span>
            </div>
            <div class="lead-card-input">
              {{ asLeadCard(item).placeholder || '您的号码' }}
            </div>
            <div class="lead-card-btn">
              {{ asLeadCard(item).btn_text || '立即参与' }}
            </div>
            <div class="lead-card-privacy">
              <span>已阅读并同意《</span>
              <span class="text-blue-400">{{ asLeadCard(item).privacy_name || '隐私协议' }}</span>
              <span>》</span>
            </div>
          </el-card>
        </template>
        <el-empty v-else class="grid-empty" :description="emptyText" />
      </div>
      <TablePagination
        v-if="total > 0"
        v-model:page="innerPage"
        v-model:page-size="innerPageSize"
        :total="total"
        :page-sizes="[12, 24]"
        @page-change="onPaginationChange"
        @size-change="onPaginationChange"
      />
    </template>

    <!-- ── 表格布局（其他类型）────────────────────────────────────────── -->
    <template v-else>
      <DataTableWithPagination
        v-model:page="innerPage"
        v-model:page-size="innerPageSize"
        :data="list"
        :loading="loading"
        :total="total"
        :show-column-settings="false"
        :row-key="rowKey"
        :page-sizes="pageSizes"
        :show-total="!isCursorPaged"
        padding="0"
        @page-change="onPaginationChange"
        @size-change="onPaginationChange"
      >
        <!-- 自定义选择列：完全由 selectedIds prop 驱动，避免 el-table 数据刷新时清空选中 -->
        <el-table-column width="80">
          <template v-if="!showOrder" #header>
            <el-checkbox
              :model-value="allCurrentPageSelected"
              :indeterminate="someCurrentPageSelected && !allCurrentPageSelected"
              @change="toggleSelectAll"
              @click.stop
            />
          </template>
          <template v-else #header>
            <span class="order-col-header">序号</span>
          </template>
          <template #default="{ row }">
            <!-- 普通模式：checkbox -->
            <div v-if="!showOrder" class="order-cell" @click.stop="toggleSelect(row[rowKey])">
              <el-checkbox :model-value="isSelected(row[rowKey])" />
            </div>
            <!-- 顺序发送模式：序号 -->
            <div v-else class="order-cell" @click.stop="toggleSelect(row[rowKey])">
              <div v-if="isSelected(row[rowKey])" class="order-badge-sm">
                {{ getOrderIndex(row[rowKey]) }}
              </div>
              <div v-else class="order-cell-empty" />
            </div>
          </template>
        </el-table-column>
        <el-table-column
          v-for="col in columns"
          :key="col.prop"
          :prop="col.type === 'image' ? undefined : col.prop"
          :label="col.label"
          :width="col.width"
        >
          <template v-if="col.type === 'image'" #default="{ row }">
            <el-image
              v-if="row[col.prop]"
              :src="row[col.prop]"
              :preview-src-list="[row[col.prop]]"
              preview-teleported
              fit="cover"
              class="material-thumb"
            />
          </template>
          <template v-else-if="col.type === 'onlineStatus'" #default="{ row }">
            <el-tag :type="isOnline(row[col.prop]) ? 'success' : 'info'" size="small">
              {{ isOnline(row[col.prop]) ? '上架' : '未上架' }}
            </el-tag>
          </template>
        </el-table-column>
      </DataTableWithPagination>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import DataTableWithPagination from '@/components/DataTableWithPagination.vue';
import TablePagination from '@/components/TablePagination.vue';
import { apiGet, apiPost } from '@/api';
import {
  getMaterialConfig,
  DY_PRODUCT_ONLINE_STATUS,
  KEYWORD_SEARCHABLE_APIS,
  KEYWORD_SEARCH_FIELD,
} from '@/constants/channelMaterials';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/constants/api';
import type {
  ChannelMaterialType,
  ChannelMaterialItem,
  MaterialTableColumn,
} from '@/types/channelMaterials';
import type { MenuTemplate, WebLeadCard } from '@/types/chatBox';

/** 上架状态：只有 1 算上架，其余取值都归未上架 */
const isOnline = (value: unknown) => Number(value) === DY_PRODUCT_ONLINE_STATUS;

// ── Props & Emits ────────────────────────────────────────────────────────
const props = withDefaults(
  defineProps<{
    channelKey: string;
    materialType: ChannelMaterialType;
    accountId: number;
    /** 已选中的 ID 列表（不同渠道字段类型可能不同，故同时支持 number/string） */
    selectedIds?: (number | string)[];
    /** single: 单选（对话框用）; multiple: 多选（方案配置用） */
    selectionMode?: 'single' | 'multiple';
    /** 是否显示勾选顺序序号（顺序发送模式启用） */
    showOrder?: boolean;
  }>(),
  {
    selectedIds: () => [],
    selectionMode: 'multiple',
    showOrder: false,
  },
);

const emit = defineEmits<{
  'update:selectedIds': [ids: (number | string)[]];
  change: [items: ChannelMaterialItem[]];
  loaded: [list: ChannelMaterialItem[]];
}>();

// ── 渲染模式判断 ─────────────────────────────────────────────────────────
const isMenuType = computed(
  () => props.materialType === 'qa_menu' || props.materialType === 'text_menu',
);
const isWebLeadCard = computed(
  () => props.channelKey === 'web' && props.materialType === 'liuzi_card',
);
/** 图片库素材：所有渠道共用同一批图片，走卡片布局 */
const isImageType = computed(() => props.materialType === 'image');
/** 卡片类布局一页 12 张，表格类沿用默认分页大小 */
const isCardLayout = computed(() => isMenuType.value || isImageType.value);

// ── 数据加载 ─────────────────────────────────────────────────────────────
const list = ref<ChannelMaterialItem[]>([]);
const loading = ref(false);
const total = ref(0);
const innerPage = ref(1);
const innerPageSize = ref(isMenuType.value || isImageType.value ? 12 : DEFAULT_PAGE_SIZE);

const config = computed(() => {
  const raw = getMaterialConfig(props.channelKey, props.materialType);
  if (!raw) return null;
  /** 自建物料接口统一支持 keyword 搜索，避免在几十处配置里逐个声明 */
  if (!raw.searchField && KEYWORD_SEARCHABLE_APIS.includes(raw.apiPath)) {
    return { ...raw, searchField: KEYWORD_SEARCH_FIELD };
  }
  return raw;
});
const columns = computed((): MaterialTableColumn[] => config.value?.columns ?? []);
const rowKey = computed(() => config.value?.rowKey ?? 'id');
const emptyText = computed(() => {
  if (isMenuType.value) return '暂无菜单，请先添加';
  if (isImageType.value) return '暂无图片，请先到素材管理 - 图片库上传';
  return '暂无数据，请先添加';
});

/** 本次数据是否来自游标分页接口（只回 has_more、不回 total） */
const isCursorPaged = ref(false);

/** 每页条数选项：接口有上限时（如商品列表 50）过滤掉超限项，避免选中后静默返回空 */
const pageSizes = computed(() => {
  const max = config.value?.maxPageSize;
  return max ? PAGE_SIZE_OPTIONS.filter((size) => size <= max) : PAGE_SIZE_OPTIONS;
});

/** 搜索框输入值（仅 config.searchField 声明的类型可见） */
const searchValue = ref('');

/** 精确搜索为整表重查，需回到第一页 */
const onSearch = () => {
  innerPage.value = 1;
  loadData();
};

const loadData = async () => {
  if (!config.value?.apiPath || props.accountId == null) {
    list.value = [];
    total.value = 0;
    return;
  }
  loading.value = true;
  try {
    const pageSizeKey = config.value.pageSizeKey ?? 'page_size';
    const searchKey = config.value.searchField?.key;
    const keyword = searchValue.value.trim();
    const params = {
      /** 图片库这类通用素材落库 account_id=0，带上当前账号会查不到 */
      ...(config.value.skipAccountId ? {} : { account_id: props.accountId }),
      page: innerPage.value,
      [pageSizeKey]: innerPageSize.value,
      ...config.value.extraParams,
      ...(searchKey && keyword ? { [searchKey]: keyword } : {}),
    };
    const apiFn = config.value.method === 'GET' ? apiGet : apiPost;
    const res = (await apiFn(config.value.apiPath, params)) as Record<string, unknown>;

    const listKey = config.value.listKey ?? 'list';
    let rawList = (res[listKey] ?? res.data ?? []) as ChannelMaterialItem[];
    if (config.value.transform) {
      rawList = rawList.map((item) =>
        config.value!.transform!(item as unknown as Record<string, unknown>),
      ) as ChannelMaterialItem[];
    }
    list.value = rawList;
    /**
     * 游标分页（如抖音商品列表）：接口只回 has_more 不回 total，直接用 total=0 会让
     * el-pagination 认为没有数据、翻页按钮全禁用。这里按 has_more 推算一个虚拟总数，
     * 让「下一页」在还有数据时可点、翻到底时自动禁用。
     */
    isCursorPaged.value = typeof res.has_more === 'boolean';
    if (searchKey && keyword) {
      /**
       * 精确搜索时接口忽略分页、回传的 page_size 只反映本次命中条数，
       * 若拿它覆盖每页条数，清空搜索后分页会被永久改小，故搜索分支只按结果数计总数
       */
      isCursorPaged.value = false;
      total.value = rawList.length;
    } else if (isCursorPaged.value) {
      /**
       * 该校正只为「服务端收敛超限值」而设（如请求 100 实际生效 50），
       * 因此仅采纳仍属于可选项的回传值；异常值（如 1）一律忽略，避免把分页改坏
       */
      const returned = Number(res.page_size);
      const effectivePageSize = pageSizes.value.includes(returned) ? returned : innerPageSize.value;
      innerPageSize.value = effectivePageSize;
      total.value = res.has_more
        ? innerPage.value * effectivePageSize + 1
        : (innerPage.value - 1) * effectivePageSize + rawList.length;
    } else {
      total.value = (res.total as number) ?? 0;
    }
  } finally {
    loading.value = false;
    emit('loaded', list.value);
  }
};

let suppressPaginationLoad = false;

const onPaginationChange = () => {
  if (suppressPaginationLoad) return;
  loadData();
};

// 切换渠道/类型/账号时重置并重新加载
watch(
  () => [props.channelKey, props.materialType, props.accountId],
  async () => {
    suppressPaginationLoad = true;
    innerPage.value = 1;
    innerPageSize.value = isCardLayout.value ? 12 : DEFAULT_PAGE_SIZE;
    await nextTick();
    suppressPaginationLoad = false;
    loadData();
  },
  { immediate: true },
);

// ── 选中逻辑 ─────────────────────────────────────────────────────────────
type RawId = number | string;

const isSelected = (id: RawId | undefined | null) =>
  id !== undefined && id !== null && props.selectedIds.includes(id as RawId);

/** 当前页所有行的 ID 集合（保留原始类型，可能是 number 或 string） */
const currentPageIds = computed<RawId[]>(() => {
  const key = rowKey.value;
  return list.value
    .map((item) => (item as Record<string, unknown>)[key] as RawId)
    .filter((v) => v !== undefined && v !== null);
});

/** 当前类型已选 ID 列表（按原始顺序，仅含当前列表中存在的 ID），用于计算序号 */
const currentTypeSelectedIds = computed(() => {
  const idSet = new Set(currentPageIds.value);
  return props.selectedIds.filter((id) => idSet.has(id));
});

const getOrderIndex = (id: RawId) => currentTypeSelectedIds.value.findIndex((v) => v === id) + 1;

const allCurrentPageSelected = computed(
  () => currentPageIds.value.length > 0 && currentPageIds.value.every((id) => isSelected(id)),
);

const someCurrentPageSelected = computed(() => currentPageIds.value.some((id) => isSelected(id)));

const toggleSelectAll = (checked: boolean | string | number) => {
  const ids = currentPageIds.value;
  const nextIds = checked
    ? [...props.selectedIds, ...ids.filter((id) => !isSelected(id))]
    : props.selectedIds.filter((id) => !ids.includes(id));
  emit('update:selectedIds', nextIds);
  const key = rowKey.value;
  emit(
    'change',
    list.value.filter((m) => nextIds.includes((m as Record<string, unknown>)[key] as RawId)),
  );
};

const toggleSelect = (id: RawId | undefined | null) => {
  if (id === undefined || id === null) return;
  const rawId = id as RawId;
  let nextIds: RawId[];
  if (props.selectionMode === 'single') {
    nextIds = isSelected(rawId) ? [] : [rawId];
  } else {
    nextIds = isSelected(rawId)
      ? props.selectedIds.filter((v) => v !== rawId)
      : [...props.selectedIds, rawId];
  }
  emit('update:selectedIds', nextIds);
  const key = rowKey.value;
  emit(
    'change',
    list.value.filter((m) => nextIds.includes((m as Record<string, unknown>)[key] as RawId)),
  );
};

// ── 类型辅助 ─────────────────────────────────────────────────────────────
const asMenu = (item: ChannelMaterialItem) => item as unknown as MenuTemplate;
const asLeadCard = (item: ChannelMaterialItem) => item as unknown as WebLeadCard;
const asImage = (item: ChannelMaterialItem) =>
  item as unknown as { id?: number; name?: string; url?: string; content?: { url?: string } };
/** 图片地址：url 为主，兼容只回了 content.url 的数据 */
const imageSrc = (item: ChannelMaterialItem): string => {
  const it = asImage(item);
  return it.url || it.content?.url || '';
};
const getMenuId = (item: ChannelMaterialItem) => Number((item as Record<string, unknown>).id ?? 0);

function parseQuestions(raw?: string): unknown[] {
  if (!raw) return [];
  try {
    return (JSON.parse(raw) as { questions?: unknown[] })?.questions ?? [];
  } catch {
    return [];
  }
}

function parseMeta(raw?: string): { conclusion?: string } | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as { conclusion?: string };
  } catch {
    return null;
  }
}

function getQuestionTitle(q: unknown): string {
  if (!q || typeof q !== 'object') return String(q ?? '');
  const obj = q as Record<string, unknown>;
  return String(obj.title ?? obj.text ?? obj.name ?? '');
}

defineExpose({ reload: loadData });
</script>

<style lang="scss" scoped>
/** 精确搜索：与操作行同排，margin-left:auto 推到行尾与左侧操作两端对齐 */
.material-search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.material-search-input {
  width: 240px;
}

.material-list-display {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;

  /**
   * 表格区传了 padding=0（表格自身要贴边），分页跟着贴到容器边界上，
   * 纵向滚动条一出现就会把最左侧的「上一页」按钮切掉半个，这里单独补回内边距。
   */
  :deep(.data-table-pagination) {
    padding: 0 12px 4px;
  }
}

.header-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

// ── 卡片公共 ──
.card-grid {
  min-height: 80px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  align-content: start;
}

// 缺省态横跨整行，使其在弹窗内水平居中
.grid-empty {
  grid-column: 1 / -1;
}

.menu-card,
.image-card {
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: border-color 0.2s;

  &.is-selected {
    border-color: var(--el-color-primary);
  }
}

.image-card-img {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100px;
  overflow: hidden;
  font-size: 12px;
  color: #c0c4cc;
  background: #f5f7fa;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
}

.image-card-img-inner {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-card-name {
  margin-top: 8px;
  overflow: hidden;
  font-size: 13px;
  color: #475569;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-card {
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: border-color 0.2s;

  &:deep(.el-card__body) {
    padding: 12px;
  }

  &.is-selected {
    border-color: var(--el-color-primary);
  }
}

// 卡片右上角序号徽章
.order-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 1;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--el-color-primary);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

// ── 菜单卡片 ──
.card-title {
  font-weight: 500;
  font-size: 13px;
  color: #303133;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.menu-info {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* 内容过多时在卡片内纵向滚动，避免卡片被撑高 */
  max-height: 160px;
  overflow-y: auto;
}

.menu-info-row {
  font-size: 12px;
  color: #606266;
  display: flex;
  gap: 4px;
  line-height: 1.5;
  word-break: break-word;
}

// ── 留资卡片 ──
.lead-card-img {
  width: 100%;
  height: 100px;
  background: #f5f7fa;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 12px;
  margin-bottom: 8px;
  overflow: hidden;
}

.lead-card-img-inner {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.lead-card-input {
  height: 32px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 0 10px;
  font-size: 12px;
  color: #c0c4cc;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.lead-card-btn {
  height: 32px;
  background: var(--el-color-primary);
  color: #fff;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  margin-bottom: 8px;
}

.lead-card-privacy {
  font-size: 11px;
  color: #909399;
}

// ── 缩略图 ──
.material-thumb {
  width: 60px;
  height: 60px;
  border-radius: 4px;
}

// ── 顺序发送序号列 ──
.order-col-header {
  font-size: 12px;
  color: #606266;
}

.order-cell {
  display: flex;
  align-items: center;
  cursor: pointer;
  height: 100%;
}

.order-badge-sm {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--el-color-primary);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.order-cell-empty {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid #d0d0d0;
}
</style>
